import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/TeacherSettings.css';

const TeacherSettings = () => {
  const { user, userType, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  // Profile data
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    subject: '',
    school: ''
  });

  // Password data
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  // Settings data
  const [settingsData, setSettingsData] = useState({
    default_subject: '',
    default_requires_submission: true,
    default_show_answer_key: false,
    notifications_enabled: true,
    deadline_reminder_days: 3
  });

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 5000);
  };

  const handleAuthError = useCallback((response) => {
    if (response.status === 401) {
      showMessage('error', 'Sessão expirada. Redirecionando para login...');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
      return true;
    }
    return false;
  }, [logout, navigate]);

  const loadProfileData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError({ status: 401 });
        return;
      }

      console.log('🔍 Loading profile data...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/teacher/profile`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📡 Profile response status:', response.status);

      if (handleAuthError(response)) return;

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Profile data loaded:', result);
        setProfileData(result.teacher);
      } else {
        const error = await response.json();
        console.error('❌ Profile load error:', error);
        showMessage('error', error.error || 'Erro ao carregar dados do perfil');
      }
    } catch (error) {
      console.error('❌ Profile load exception:', error);
      showMessage('error', 'Erro ao carregar dados do perfil');
    }
  }, [handleAuthError]);

  const loadSettingsData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError({ status: 401 });
        return;
      }

      console.log('🔍 Loading settings data...');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/teacher/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      console.log('📡 Settings response status:', response.status);

      if (handleAuthError(response)) return;

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Settings data loaded:', result);
        setSettingsData(result.settings);
      } else {
        const error = await response.json();
        console.error('❌ Settings load error:', error);
        // Settings são opcionais, não mostrar erro se não existirem
        console.log('📝 Using default settings');
      }
    } catch (error) {
      console.error('❌ Settings load exception:', error);
      // Settings são opcionais, não mostrar erro
      console.log('📝 Using default settings due to error');
    }
  }, [handleAuthError]);

  // Check authentication on component mount
  useEffect(() => {
    if (!user || userType !== 'teacher') {
      showMessage('error', 'Acesso negado. Redirecionando para login...');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      showMessage('error', 'Token não encontrado. Redirecionando para login...');
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
      return;
    }

    loadProfileData();
    loadSettingsData();
  }, [user, userType, logout, navigate, loadProfileData, loadSettingsData]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError({ status: 401 });
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/teacher/update-profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profileData)
      });

      if (handleAuthError(response)) {
        setLoading(false);
        return;
      }

      const result = await response.json();

      if (response.ok) {
        showMessage('success', result.message);
        setProfileData(result.teacher);
      } else {
        showMessage('error', result.error);
      }
    } catch (error) {
      showMessage('error', 'Erro ao atualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (passwordData.new_password !== passwordData.confirm_password) {
      showMessage('error', 'Nova senha e confirmação não coincidem');
      setLoading(false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError({ status: 401 });
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/teacher/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (handleAuthError(response)) {
        setLoading(false);
        return;
      }

      const result = await response.json();

      if (response.ok) {
        showMessage('success', result.message);
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });
      } else {
        showMessage('error', result.error);
      }
    } catch (error) {
      showMessage('error', 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  const handleSettingsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        handleAuthError({ status: 401 });
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/teacher/update-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settingsData)
      });

      if (handleAuthError(response)) {
        setLoading(false);
        return;
      }

      const result = await response.json();

      if (response.ok) {
        showMessage('success', result.message);
      } else {
        showMessage('error', result.error);
      }
    } catch (error) {
      showMessage('error', 'Erro ao atualizar configurações');
    } finally {
      setLoading(false);
    }
  };

  // Se não está autenticado, mostrar mensagem de carregamento
  if (!user || userType !== 'teacher') {
    return (
      <div className="teacher-settings">
        <div className="settings-header">
          <h1>🔐 Verificando Acesso...</h1>
          <p>Aguarde enquanto verificamos suas credenciais</p>
        </div>
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="teacher-settings">
      <div className="settings-header">
        <h1>⚙️ Configurações</h1>
        <p>Gerencie seu perfil, senha e preferências do sistema</p>
      </div>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="settings-tabs">
        <button
          className={`tab-button ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          👤 Perfil
        </button>
        <button
          className={`tab-button ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
        >
          🔒 Senha
        </button>
        <button
          className={`tab-button ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Configurações
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'profile' && (
          <div className="tab-panel">
            <h2>📝 Dados do Perfil</h2>
            <form onSubmit={handleProfileSubmit} className="settings-form">
              <div className="form-group">
                <label htmlFor="name">Nome Completo *</label>
                <input
                  type="text"
                  id="name"
                  value={profileData.name}
                  onChange={(e) => setProfileData({...profileData, name: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({...profileData, email: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="subject">Matéria Principal *</label>
                <input
                  type="text"
                  id="subject"
                  value={profileData.subject}
                  onChange={(e) => setProfileData({...profileData, subject: e.target.value})}
                  required
                  placeholder="Ex: Matemática, Português, História..."
                />
              </div>

              <div className="form-group">
                <label htmlFor="school">Escola *</label>
                <input
                  type="text"
                  id="school"
                  value={profileData.school}
                  onChange={(e) => setProfileData({...profileData, school: e.target.value})}
                  required
                  placeholder="Nome da sua escola"
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? '⏳ Salvando...' : '💾 Salvar Perfil'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'password' && (
          <div className="tab-panel">
            <h2>🔐 Alterar Senha</h2>
            <form onSubmit={handlePasswordSubmit} className="settings-form">
              <div className="form-group">
                <label htmlFor="current-password">Senha Atual *</label>
                <input
                  type="password"
                  id="current-password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="new-password">Nova Senha *</label>
                <input
                  type="password"
                  id="new-password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  required
                  minLength="8"
                />
                <small>Mínimo 8 caracteres, com maiúscula, minúscula e número</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirmar Nova Senha *</label>
                <input
                  type="password"
                  id="confirm-password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  required
                  minLength="8"
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? '⏳ Alterando...' : '🔑 Alterar Senha'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="tab-panel">
            <h2>⚙️ Configurações Padrão</h2>
            <form onSubmit={handleSettingsSubmit} className="settings-form">
              <div className="form-group">
                <label htmlFor="default-subject">Matéria Padrão</label>
                <input
                  type="text"
                  id="default-subject"
                  value={settingsData.default_subject}
                  onChange={(e) => setSettingsData({...settingsData, default_subject: e.target.value})}
                  placeholder="Será preenchida automaticamente nas novas lições"
                />
              </div>

              <div className="checkbox-group">
                <h3>🎯 Configurações de Lição</h3>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settingsData.default_requires_submission}
                    onChange={(e) => setSettingsData({...settingsData, default_requires_submission: e.target.checked})}
                  />
                  <span className="checkmark"></span>
                  Exigir entrega por padrão
                </label>

                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settingsData.default_show_answer_key}
                    onChange={(e) => setSettingsData({...settingsData, default_show_answer_key: e.target.checked})}
                  />
                  <span className="checkmark"></span>
                  Mostrar gabarito para alunos por padrão
                </label>
              </div>

              <div className="checkbox-group">
                <h3>🔔 Notificações</h3>
                
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settingsData.notifications_enabled}
                    onChange={(e) => setSettingsData({...settingsData, notifications_enabled: e.target.checked})}
                  />
                  <span className="checkmark"></span>
                  Receber notificações do sistema
                </label>

                <div className="form-group">
                  <label htmlFor="reminder-days">Lembrar sobre prazo (dias antes)</label>
                  <input
                    type="number"
                    id="reminder-days"
                    min="0"
                    max="30"
                    value={settingsData.deadline_reminder_days}
                    onChange={(e) => setSettingsData({...settingsData, deadline_reminder_days: parseInt(e.target.value)})}
                  />
                  <small>Quantos dias antes do prazo você quer ser lembrado</small>
                </div>
              </div>

              <button type="submit" className="btn-primary" disabled={loading}>
                {loading ? '⏳ Salvando...' : '⚙️ Salvar Configurações'}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherSettings; 